"use client";
// 26차 후속 — 운영대시보드 위젯 1차: 미니 달력.
// 기존 /calendar/events 월 단위 조회만 사용. 신규 endpoint/DB 변경 없음.
// 날짜 클릭 상세 모달 등 확장 기능은 1차 범위 밖.

import { useEffect, useMemo, useState } from "react";
import { getCalendarEvents, type CalendarEvent } from "@/lib/api";

const EVENT_TYPE_COLOR: Record<string, string> = {
  company_event: "bg-blue-500",
  deadline: "bg-red-500",
  holiday: "bg-rose-400",
  personal_schedule: "bg-emerald-500",
  vacation: "bg-amber-500",
};

function eventTypeColor(t: string | null | undefined): string {
  if (!t) return "bg-gray-400";
  return EVENT_TYPE_COLOR[t] || "bg-gray-400";
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function MiniCalendarCard() {
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCalendarEvents({ year, month })
      .then((rows) => {
        if (!cancelled) setEvents(rows);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, month]);

  // 날짜별 event_type 목록 (start_date ~ end_date 포함 범위)
  const dotsByDate = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const ev of events) {
      const start = new Date(ev.start_date);
      const end = new Date(ev.end_date || ev.start_date);
      // 비정상 범위 방어
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
      const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      while (cur <= last) {
        const key = ymd(cur);
        if (!map.has(key)) map.set(key, new Set());
        map.get(key)!.add(ev.event_type || "unknown");
        cur.setDate(cur.getDate() + 1);
      }
    }
    return map;
  }, [events]);

  // 6×7 그리드 (월의 첫 주 일요일 ~ 마지막 주 토요일)
  const cells = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const startWeekday = first.getDay(); // 0=Sun
    const gridStart = new Date(year, month - 1, 1 - startWeekday);
    const arr: { date: Date; inMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      arr.push({ date: d, inMonth: d.getMonth() === month - 1 });
    }
    return arr;
  }, [year, month]);

  const monthLabel = `${year}년 ${String(month).padStart(2, "0")}월`;

  function shiftMonth(delta: number) {
    let m = month + delta;
    let y = year;
    while (m < 1) {
      m += 12;
      y -= 1;
    }
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    setYear(y);
    setMonth(m);
  }

  const todayKey = ymd(today);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-gray-700">📅 미니 달력</p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            className="px-2 py-0.5 text-xs rounded border border-gray-200 hover:bg-gray-50"
            aria-label="이전 달"
          >
            ◀
          </button>
          <span className="text-xs font-semibold text-gray-700 min-w-[88px] text-center">
            {monthLabel}
          </span>
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            className="px-2 py-0.5 text-xs rounded border border-gray-200 hover:bg-gray-50"
            aria-label="다음 달"
          >
            ▶
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-gray-500 mb-1">
        {["일", "월", "화", "수", "목", "금", "토"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map(({ date, inMonth }) => {
          const key = ymd(date);
          const types = Array.from(dotsByDate.get(key) ?? []);
          const isToday = key === todayKey;
          return (
            <div
              key={key}
              data-testid="mini-calendar-cell"
              className={`min-h-[40px] rounded border text-[11px] p-1 flex flex-col items-center justify-start ${
                isToday
                  ? "border-blue-400 bg-blue-50"
                  : inMonth
                    ? "border-gray-100 bg-white"
                    : "border-gray-50 bg-gray-50 text-gray-300"
              }`}
            >
              <span className={`font-semibold ${isToday ? "text-blue-700" : ""}`}>
                {date.getDate()}
              </span>
              {types.length > 0 && (
                <div className="flex flex-wrap gap-0.5 mt-0.5 justify-center">
                  {types.slice(0, 3).map((t, i) => (
                    <span
                      key={`${key}-${t}-${i}`}
                      className={`inline-block w-1.5 h-1.5 rounded-full ${eventTypeColor(t)}`}
                      title={t}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loading && (
        <p className="mt-2 text-[10px] text-gray-400">불러오는 중...</p>
      )}
    </div>
  );
}
